import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiProvider } from "@src/store/ApiProvider";
import { pricingActions } from "..";
import { RootState } from "@src/store/store";
import { EventInventoryRecord, getInventoryCacheEventId } from "@src/store/pricing/queryEventInventoryAllSlice";
import { handleError } from "@src/store/getThunkAndSlice";

export interface IHoldListingsParams {
	ListingIds: Array<number>;
	Comments: string;
	ExpirationDate: string;
}

const buildFormBody = (params: IHoldListingsParams): URLSearchParams => {
	const body = new URLSearchParams();
	params.ListingIds.forEach((id) => body.append("ListingIds[]", String(id)));
	body.append("Comments", params.Comments ?? "");
	body.append("ExpirationDate", params.ExpirationDate ?? "");
	return body;
};

export const holdListings = createAsyncThunk<boolean, IHoldListingsParams>(
	"pricing/holdListings",
	async (params, { dispatch, rejectWithValue, getState }) => {
		try {
			const body = buildFormBody(params);
			await ApiProvider.default.axios.post(`/api/Pricing/HoldListings?_dc=${Date.now()}`, body, {
				headers: { "Content-Type": "application/x-www-form-urlencoded" },
			});

			const rootState = getState() as RootState;
			const heldIds = new Set(params.ListingIds);
			const comments = params.Comments ?? "";

			const newData = rootState.pricing.eventInventoryAll.data.map((item) => {
				if (!heldIds.has(item.Listing_ID)) return item;
				return { ...item, On_Hold: true, On_Hold_Comments: comments };
			});
			const eventId = getInventoryCacheEventId(rootState);
			dispatch(pricingActions.updateInventoryAll({ data: newData, eventId }));

			if (rootState.pricing.globalInventory.data.length) {
				const newGlobalData = (rootState.pricing.globalInventory.data as Array<EventInventoryRecord>).map((item) => {
					if (!heldIds.has(item.Listing_ID)) return item;
					return { ...item, On_Hold: true, On_Hold_Comments: comments };
				});
				dispatch(pricingActions.updateGlobalInventory({ data: newGlobalData }));
			}

			return true;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	},
);

export const holdListingsSlice = createSlice({
	name: "pricing/holdListings",
	initialState: { loading: false, error: null as string | null },
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(holdListings.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(holdListings.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(holdListings.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) ?? null;
			});
	},
});
